import { NgIf, CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, SimpleChange, ViewEncapsulation, forwardRef, inject } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatDrawerToggleResult } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FileManagerService } from 'app/modules/admin/apps/file-manager/file-manager.service';
import { Item, ItemLabel } from 'app/modules/admin/apps/file-manager/file-manager.types';
import { FileManagerListComponent } from 'app/modules/admin/apps/file-manager/list/list.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { FileManagerComponent } from 'app/modules/admin/apps/file-manager/file-manager.component';
import { FuseFindByKeyPipe } from '@fuse/pipes/find-by-key/find-by-key.pipe';
import { labelColorDefs } from 'app/shared/global-constants';
import { DocumentService } from 'app/services/document.service';
import { Asset } from 'app/models/document';
import { NgxLoadingModule } from 'ngx-loading';
import { LabelService } from 'app/services/label.service';
import { Label } from 'app/models/label';
import { Util } from 'app/models/util';
import { LpmLabelPipe } from '@fuse/pipes/lpm-label.pipe';
import { ToastrService } from 'ngx-toastr';
import { FavouriteService } from 'app/services/favourite.service';
import { EventService } from 'app/services/event.service';
import { ActivitiesComponent } from '../../activities/activities.component';
import { NotesComponent } from '../../notes/notes.component';
import { PermissionsComponent } from '../../permissions/permissions.component';
import { FavoriteComponent } from '../favorite.component';
import { FileSizePipe } from '@fuse/pipes/file-size.pipe';
import { FullNamePipe } from '@fuse/pipes/full-name.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DomSanitizer } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IsDisplayedDirective } from '@fuse/directives/isDisplayed.directive';

@Component({
    selector: 'favorite-details',
    templateUrl: './favorite-details.component.html',
    styleUrls: ['./favorite-details.component.scss'],
    // encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatRippleModule,
        RouterLink,
        MatInputModule,
        MatMenuModule,
        MatIconModule,
        NgIf,
        MatTabsModule,
        FileManagerComponent,
        ActivitiesComponent,
        NotesComponent,
        PermissionsComponent,
        FuseFindByKeyPipe,
        NgClass,
        NgxLoadingModule,
        FileSizePipe,
        FullNamePipe,
        FormsModule, ReactiveFormsModule, MatFormFieldModule,
        MatProgressSpinnerModule,
        forwardRef(() => IsDisplayedDirective)
    ],
})

export class FavouriteDetailsComponent implements OnInit, OnDestroy {

    @Input() docId = null;
    @Input() selectedTab = 0;
    configForm: UntypedFormGroup;
    isLoading: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Get the label colors
    labelColors = labelColorDefs;

    labels: Label[] = [];

    asset: Asset = new Asset();
    public modalRef;
    public selectedAsset: Asset = null;
    subs = new Subscription();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private favourite: FavoriteComponent,
        public _fileManagerService: FileManagerService,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        public _fileManagerComponent: FileManagerComponent,
        private _router: Router,
        private route: ActivatedRoute,
        private docService: DocumentService,
        private favService: FavouriteService,
        private labelService: LabelService,
        public util: Util,
        private toastr: ToastrService,
        private eventSer: EventService,
        private _matDialog: MatDialog,
        public sanitizer: DomSanitizer,
        private labelPipe: LpmLabelPipe,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        for (let propName in changes) {
            let changedProp = changes[propName];
            let to = changedProp.currentValue; //JSON.stringify();
            if (changedProp.isFirstChange()) {
            } else {
                let from = JSON.stringify(changedProp.previousValue);
                if (`${propName}` == `docId`) {
                    this.docId = to;
                    if (this.docId && this.util.getIsWebApp()) this.getDoc();
                }
            }
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Open the drawer
        console.log('details oninit');
        this.route.paramMap.subscribe((param) => {
            if(param.get("id") && !this.util.getIsWebApp()){
                this.docId = param.get("id");
                this.getDoc();
            }
        });

        this.route.queryParams.subscribe(params => {
            this.selectedTab = 0;
            if (params['activeTab'] && !this.util.getIsWebApp()) {
                this.selectedTab = +params['activeTab']; // '+' converts the string to a number
            }
        });

        this.subs.add(
            this.eventSer.getData.subscribe((data) => {
                if(this.docId)
                this.getDoc()
            })
        );

        this.subs.add(
            this.eventSer.multiRowSelect.subscribe(() => {
                    if (this.util.getIsWebApp()) {
                        this.closeDrawer();
                    } else {
                        this._router.navigate(['../../'], { relativeTo: this.route }); 
                    }
            })
        );

        if(!this.util.getIsWebApp()) this.openClosePanel();

        // Build the config form
        this.configForm = this._formBuilder.group(
            this.util.trashForm
        );
    }

    openClosePanel() {
        // Get the item
        this._fileManagerService.item$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((item: Item) => {
                // Open the drawer in case it is closed
                this.favourite.matDrawer.open();

                // Get the item
                //this.item = item;
                console.log('item', item);

                // Mark for check
                this._changeDetectorRef.markForCheck();

            })
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.subs.unsubscribe();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Retrieves the document by its ID.
     * Sets the `isLoading` flag to true before making the API call.
     * Upon successful retrieval, assigns the data to the `asset` property and sets `isLoading` flag to false.
     * If an error occurs, sets `isLoading` flag to false and logs the error to the console.
     */
    getDoc() {
        this.getLabels();
        this.isLoading = true;
        this.docService.getDocById(this.docId).then((res) => {
            this.asset = this.docService.getData(res);
            this.isLoading = false;
            if(this.util.getIsWebApp())
            this.openClosePanel();

        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    /**
     * Retrieves the labels from the label service.
     * Sets the isLoading flag to true before making the API call.
     * Updates the labels property with the response data.
     * Sets the isLoading flag to false after the API call is completed.
     * Logs any errors that occur during the API call.
     */
    getLabels() {
        this.isLoading = true;
        this.labelService.getLabels().then((res) => {
            this.labels = this.labelService.getData(res);
            this.isLoading = false;

        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    closeDrawer(): Promise<MatDrawerToggleResult> {
        if(this.util.getIsWebApp()) this.favourite.drawerView(null, null);
        return this.favourite.matDrawer.close();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
    * Open confirmation dialog
    * Delete the item after confirmation
    */
    openDialog_delete(assetId, type): void {
        const dialogRef = this._fuseConfirmationService.open(this.configForm.value);
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);

            if (result === 'confirmed') {
                this.trash(assetId, type);
            }
        });
    }

    /**
     * Moves the specified asset to the trash.
     * 
     * @param {string} assetId - The ID of the asset to be moved to the trash.
     * @param {string} type - The type of the asset ('file' or 'folder').
     * @returns {Promise<void>} - A promise that resolves when the asset is moved to the trash.
     */
    trash(assetId, type) {
        const ids = [assetId]
        this.docService.trash(ids).then((res) => {
            this.toastr.warning(`${type === 'file' ? 'File' : 'Folder'} moved to ${this.labelPipe.transform('Trash', 'trashLocationLabel')}`, "Trashed");
            this.closeDrawer();
            this._router.navigateByUrl("/apps/file-manager/starred");
            this.eventSer.getData.emit(true);

        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Toggle star
     */
    toggleStar(asset): void {
        asset.favourite = asset.favourite ? !asset.favourite : true;
        this.favService.favorite(asset.id, asset.favourite).then((res) => {
            if (!asset.favourite) {
                this.toastr.warning("Removed from favorite")
            } else {
                this.toastr.info("Added to favorite")
            }
            this.favourite.getAssets();
            this.eventSer.checkUnreadMessages.emit("favourite");
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Toggle label
     *
     * @param label
     */
    toggleLabel(assetId, labelId, event: MatCheckbox): void {
        if (!event.checked) {
            this.labelService.assignLabel(labelId, assetId).then((res) => {
                this.toastr.info("Label applied");
                this.favourite.getAssets();
                this.getDoc();
            }).catch((error) => {
                console.log(error);
            })
        } else this.deleteLabel(labelId, assetId);
    }

    /**
     * Checks if the given ID exists in the provided list.
     * @param id - The ID to check for.
     * @param list - The list to search in.
     * @returns `true` if the ID exists in the list, `false` otherwise.
     */
    selectedLabel(id, list: any[]) {
        if (list) {
            const index = list.findIndex(item => item.id === id)
            if (index == -1)
                return false
            else
                return true
        }
    }

    /**
     * Deletes a label from an asset.
     * 
     * @param {number} labelId - The ID of the label to be removed.
     * @param {number} assetId - The ID of the asset from which the label should be removed.
     * @returns {Promise<void>} - A promise that resolves when the label is successfully removed.
     */
    deleteLabel(labelId, assetId) {
        this.labelService.removeLabel(labelId, assetId).then((res) => {
            this.toastr.warning("Label removed");
            this.favourite.getAssets();
            this.getDoc();
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Checks if a value exists in an array.
     * 
     * @param array - The array to check.
     * @param value - The value to search for.
     * @returns `true` if the value is found in the array, `false` otherwise.
     */
    checkValueInArray(array, value) {
        return array.includes(value);
    }

    goToParent(folderId: string) {
        this._router.navigate(['/apps/file-manager/folders/' + folderId]);
    }

    public desModal(modal, asset) {
        this.selectedAsset = { ...asset };
        this.modalRef = this._matDialog.open(modal);
    }

    updateDescription(): void {
        this.isLoading = true;
        this.docService.saveFolder(this.selectedAsset).then((res) => {
            this.isLoading = false;
            this.cancel();
            this.getDoc();
            this.toastr.success("Item description update successfully");
        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    cancel() {
        this.modalRef.close()
    }
}
